import { z } from "zod";
import type { ReactLocalization } from "@fluent/react";
import type { IncomeTypeEnum } from "@lending/types";

const JOB_TITLE_MAX_LENGTH = 128;
const EMPLOYER_NAME_MAX_LENGTH = 128;

interface EmploymentDetailsFormData {
  income_type: IncomeTypeEnum | undefined;
  job_title: string;
  employer_name: string;
  employment_start_month: string;
  employment_start_year: string;
}

function createEmploymentDetailsSchema(l10n: ReactLocalization) {
  return z
    .object({
      income_type: z
        .string()
        .min(1, { message: l10n.getString("error-required", null, "Required") }),
      job_title: z.string(),
      employer_name: z.string(),
      employment_start_month: z.string(),
      employment_start_year: z.string(),
    })
    .superRefine((data, ctx) => {
      const incomeType = data.income_type as IncomeTypeEnum;

      // Employment field requirements vary by income type:
      // - retirement: no follow-up fields
      // - other: description only (stored in job_title)
      // - all other income types: job title, company, and start date
      if (incomeType === "retirement") {
        return;
      }

      if (data.job_title.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["job_title"],
        });
      } else if (data.job_title.length > JOB_TITLE_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString(
            "error-max-characters",
            { max: JOB_TITLE_MAX_LENGTH },
            `Must be ${JOB_TITLE_MAX_LENGTH} characters or fewer`
          ),
          path: ["job_title"],
        });
      }

      if (incomeType === "other") {
        return;
      }

      if (data.employer_name.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["employer_name"],
        });
      } else if (data.employer_name.length > EMPLOYER_NAME_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString(
            "error-max-characters",
            { max: EMPLOYER_NAME_MAX_LENGTH },
            `Must be ${EMPLOYER_NAME_MAX_LENGTH} characters or fewer`
          ),
          path: ["employer_name"],
        });
      }

      if (data.employment_start_month.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["employment_start_month"],
        });
      }

      if (data.employment_start_year.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["employment_start_year"],
        });
      }

      if (!data.employment_start_month || !data.employment_start_year) {
        return;
      }

      const month = Number(data.employment_start_month);
      const year = Number(data.employment_start_year);
      const today = new Date();
      const earliestYear = today.getFullYear() - 80;

      // Combobox accepts free-form text, so guard against non-numeric or out-of-range
      // values that would otherwise pass through to the backend.
      const monthInvalid = !Number.isInteger(month) || month < 1 || month > 12;
      const yearInvalid =
        !Number.isInteger(year) || year < earliestYear || year > today.getFullYear();

      if (monthInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["employment_start_month"],
        });
      }

      if (yearInvalid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString("error-required", null, "Required"),
          path: ["employment_start_year"],
        });
      }

      if (monthInvalid || yearInvalid) {
        return;
      }

      if (
        year > today.getFullYear() ||
        (year === today.getFullYear() && month > today.getMonth() + 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: l10n.getString(
            "error-start-date-future",
            null,
            "Start date cannot be in the future"
          ),
          path: ["employment_start_year"],
        });
      }
    });
}

function formatEmploymentStartDate(
  month: number | null | undefined,
  year: number | null | undefined
) {
  return {
    employment_start_month: month ? String(month).padStart(2, "0") : "",
    employment_start_year: year ? String(year) : "",
  };
}

function getEmploymentFieldResetPayload(incomeType: IncomeTypeEnum) {
  if (incomeType === "retirement") {
    return {
      job_title: null,
      employer_name: null,
      employment_start_month: null,
      employment_start_year: null,
    };
  }

  if (incomeType === "other") {
    return {
      employer_name: null,
      employment_start_month: null,
      employment_start_year: null,
    };
  }

  return {};
}

export {
  EMPLOYER_NAME_MAX_LENGTH,
  JOB_TITLE_MAX_LENGTH,
  createEmploymentDetailsSchema,
  formatEmploymentStartDate,
  getEmploymentFieldResetPayload,
};
export type { EmploymentDetailsFormData };
